const NUMBER = /^[+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+)$/;
const magnitude = text => NUMBER.test(text) ? Number(text.replace(',', '.')) : NaN;

// Symbol-formatted angles are unambiguous; compact angles use the selected format.
export function parseCoordinateString(value, format, axis) {
  let text = String(value ?? '').trim().toUpperCase()
    .replace(/[º˚]/g, '°').replace(/[′’‘´`]/g, "'").replace(/[″“”]/g, '"');
  // A full stop copied before integer degrees is punctuation, not a DMS degree fraction.
  text = text.replace(/^\.(?=\d{1,3}\s*°\s*\d{1,2}\s*'\s*\d{1,2}(?:[.,]\d+)?\s*")/, '');
  const hemisphere = text.match(/[NSEW]$/)?.[0];
  if (hemisphere) text = text.slice(0, -1).trim();
  if ((axis === 'lat' && /[EW]/.test(hemisphere || ''))
    || (axis === 'lon' && /[NS]/.test(hemisphere || ''))) return NaN;

  const negative = text.startsWith('-');
  if (negative && /[NE]/.test(hemisphere || '')) return NaN;
  const parts = text.replace(/[°'"]/g, ' ').trim().split(/\s+/);
  const selected = /[°'"]/.test(text)
    ? ({ 1: 'dd', 2: 'dm', 3: 'dms' })[parts.length] : format;
  let degrees, minutes = 0, seconds = 0;
  if (selected === 'dd') {
    if (parts.length !== 1) return NaN;
    degrees = Math.abs(magnitude(parts[0]));
  } else if (selected === 'dm' || selected === 'dms') {
    const count = selected === 'dms' ? 3 : 2;
    if (parts.length === count) {
      if (!/^[+-]?\d{1,3}$/.test(parts[0])) return NaN;
      degrees = Math.abs(Number(parts[0])); minutes = magnitude(parts[1]);
      if (count === 3) {
        if (!/^\d{1,2}$/.test(parts[1])) return NaN;
        seconds = magnitude(parts[2]);
      }
    } else if (parts.length === 1 && !/[°'"]/.test(text)) {
      const compact = text.match(/^[+-]?(\d+)([.,]\d+)?$/);
      if (!compact) return NaN;
      const digits = compact[1], degreeDigits = digits.length - (count === 3 ? 4 : 2);
      if (degreeDigits < 1 || degreeDigits > 3) return NaN;
      degrees = Number(digits.slice(0, degreeDigits));
      minutes = Number(digits.slice(degreeDigits, degreeDigits + 2));
      const fraction = (compact[2] || '').replace(',', '.');
      if (count === 3) seconds = Number(digits.slice(degreeDigits + 2) + fraction);
      else minutes = Number(digits.slice(degreeDigits) + fraction);
    } else return NaN;
  } else return NaN;

  if (![degrees, minutes, seconds].every(Number.isFinite)
    || minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) return NaN;
  const result = degrees + minutes / 60 + seconds / 3600;
  const limit = axis === 'lat' || /[NS]/.test(hemisphere || '') ? 90 : 180;
  if (result > limit) return NaN;
  return (negative || /[SW]/.test(hemisphere || '') ? -1 : 1) * result;
}

function splitCoordinateRow(row) {
  // Preserve spaces and decimal commas inside labelled latitude/longitude values.
  const labelled = row.match(/^(.+?[NS])\s*[,;\s]+\s*(.+?[EW])\s*[,;\s]+\s*([+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+))$/i);
  if (labelled) return labelled.slice(1);
  if (row.includes(';')) return row.split(';').map(value => value.trim());
  if (row.includes(',')) return row.split(',').map(value => value.trim());
  return row.split(/\s+/);
}

export function parseCoordinateRows(text, format) {
  return text.split(/\r?\n/).map(row => row.trim()).filter(Boolean).map(row => {
    const parts = splitCoordinateRow(row);
    if (parts.length !== 3) return null;
    const lat = parseCoordinateString(parts[0], format, 'lat');
    const lon = parseCoordinateString(parts[1], format, 'lon');
    const z = magnitude(parts[2]);
    if (![lat, lon, z].every(Number.isFinite)) return null;
    return [lon, lat, z];
  }).filter(Boolean);
}
